import type {
  AddBalancePostingsCommandData,
  FractionBalancePostingSpec,
} from '@festina/life-general-types/life'
import { notEmpty, typeUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import {
  FormikForm,
  FormikPercentageInput,
  FormikSelect,
} from '@festina-life/ui/formikForms'
import {
  useFormikContext,
  useTranslate,
  useTranslateEnum,
} from '@festina-life/ui/hooks'
import { Modal } from '@festina-life/ui/structure'
import { useId, useMemo } from 'react'

import { useCommitmentVersion } from '../../../hooks/useCommitmentVersion'
import { useEnumOptions } from '../../../hooks/useEnumOptions'
import { useTranslateCover } from '../../../hooks/useTranslateCover'
import { useHook } from '../../../lenteHooks'

type Props = {
  initialValues?: FractionBalancePostingSpec
  onSubmit: (fractionBalancePostingSpec: FractionBalancePostingSpec) => void
  onClose: () => void
}

const validateKey = typeUtils.createValidator<FractionBalancePostingSpec>()

export const FractionBalancePostingSpecModal = ({
  initialValues,
  onSubmit,
  onClose,
}: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const renderPolicy = useHook('pages.commitment.useLazyRenderPolicy')()
  const translateCover = useTranslateCover()
  const formId = useId()

  const {
    values: { changeDate },
  } = useFormikContext<AddBalancePostingsCommandData>()

  const { currentCommitmentVersion: commitmentVersion } = useCommitmentVersion({
    commitmentDate: changeDate,
  })

  const accountTypes = useEnumOptions('AccountType')
  const postingTypes = useEnumOptions('PostingType')

  const { policyKeys, coverKeys } = useMemo(
    () => ({
      policyKeys:
        commitmentVersion?.policyVersions
          ?.map((policy) => policy?.policyKey)
          .filter(notEmpty) ?? [],
      coverKeys:
        commitmentVersion?.policyVersions
          ?.flatMap((policy) => [
            ...(policy?.riskCoverVersions ?? []),
            ...(policy?.savingsCoverVersions ?? []),
          ])
          .map((cover) => cover?.coverKey)
          .filter(notEmpty) ?? [],
    }),
    [commitmentVersion],
  )

  return (
    <Modal isOpen onClose={onClose}>
      <Modal.Title title={translate('action.addBalancePosting.title')} />
      <Modal.Body>
        <FormikForm
          formId={formId}
          initialValues={
            initialValues ??
            ({
              _type: 'FractionBalancePostingSpec',
            } satisfies FractionBalancePostingSpec)
          }
          onSubmit={onSubmit}
        >
          <FormikSelect
            name={validateKey('accountType')}
            label={translate('action.addBalancePosting.field.accountType')}
            options={accountTypes}
            renderFn={(accountType) =>
              translateEnum('life.enum.AccountType', accountType)
            }
          />
          <FormikSelect
            name={validateKey('postingType')}
            label={translate('action.addBalancePosting.field.postingType')}
            options={postingTypes}
            renderFn={(postingType) =>
              translateEnum('life.enum.PostingType', postingType)
            }
          />
          <FormikPercentageInput
            name={validateKey('fraction')}
            label={translate('action.addBalancePosting.field.fraction')}
          />
          <FormikSelect
            name={validateKey('policeKey')}
            label={translate('action.addBalancePosting.field.policeKey')}
            options={policyKeys}
            renderFn={renderPolicy}
          />
          <FormikSelect
            name={validateKey('coverKey')}
            label={translate('action.addBalancePosting.field.coverKey')}
            options={coverKeys}
            renderFn={translateCover}
          />
        </FormikForm>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="discreet" onClick={onClose}>
          {translate('cancel')}
        </Button>
        <Button variant="primary" type="submit" form={formId}>
          {translate('apply')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
